import React, { useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, ArrowLeft } from "lucide-react";

function GenericCreateForm({ title, endpoint, backTo, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [isSavingAndContinuing, setIsSavingAndContinuing] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    let newErrors = {};
    if (!name.trim()) {
      newErrors.name = `El nombre de ${title.toLowerCase()} es obligatorio`;
    }
    if (description.length > 255) {
      newErrors.description = `La descripción no puede exceder 255 caracteres`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await api.post(endpoint, { name, description });

      toast.success(`${title} creada exitosamente`);
      setErrors({});
      if (onCreated) onCreated();

      if (isSavingAndContinuing) {
        setName("");
        setDescription("");
        setIsSavingAndContinuing(false);
      } else {
        navigate(backTo);
      }
    } catch (error) {
      console.error(`Error al crear ${title.toLowerCase()}`, error);

      // Backend validation errors
      let backendErrors = {};
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path || "general"] = err.msg;
        });
      } else if (error.response?.data?.error) {
        backendErrors.general = error.response.data.error;
      } else {
        backendErrors.general = "Ocurrió un error inesperado";
      }

      // Asignamos los errores al estado pero no usamos toast
      setErrors(backendErrors);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a {title}s
      </button>

      <h2 className="text-3xl font-semibold text-gray-800 mb-6">
        Crear {title}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Mostrar error general */}
        {errors.general && (
          <p className="text-red-600 text-sm">{errors.general}</p>
        )}

        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre
          </label>
          <input
            type="text"
            placeholder={`Ingrese nombre de ${title.toLowerCase()}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
              errors.name
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
            }`}
          />
          {errors.name && (
            <p className="text-red-600 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <textarea
            placeholder={`Descripción de la ${title.toLowerCase()}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`w-full border px-4 py-2 rounded-lg h-28 resize-none focus:outline-none focus:ring-2 ${
              errors.description
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
            }`}
          ></textarea>
          {errors.description && (
            <p className="text-red-600 text-sm mt-1">{errors.description}</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            type="submit"
            onClick={() => setIsSavingAndContinuing(false)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow transition duration-300"
          >
            <Save size={18} />
            Crear
          </button>

          <button
            type="submit"
            onClick={() => setIsSavingAndContinuing(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow transition duration-300"
          >
            <Save size={18} />
            Crear y seguir
          </button>
        </div>
      </form>
    </div>
  );
}

export default GenericCreateForm;
